import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FaSlack, FaTelegram } from 'react-icons/fa';
import { LuArrowDown, LuArrowUp, LuFileText, LuMail } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { fetchLinkedAccounts, LinkedAccount, NotificationChannel } from '../../shared/api/notifications';
import {
  fetchNotificationSettings,
  NotificationReportTypes,
  NotificationSettings,
  updateNotificationSettings,
} from '../../shared/api/user-settings';

const CHANNEL_ORDER: NotificationChannel[] = ['slack', 'telegram', 'email'];

const CHANNEL_META: Record<NotificationChannel, { label: string; icon: React.ReactNode; iconClassName: string }> = {
  slack: { label: 'Slack', icon: <FaSlack />, iconClassName: 'text-purple-500' },
  telegram: { label: 'Telegram', icon: <FaTelegram />, iconClassName: 'text-cyan-500' },
  email: { label: 'Email', icon: <LuMail />, iconClassName: 'text-emerald-500' },
};

type NotificationPreferencesState = Omit<NotificationSettings, 'reportTypes'> & {
  reportTypes: NotificationReportTypes & { standaloneDailyReport: boolean };
};

const sortByPriority = (settings: NotificationPreferencesState): NotificationChannel[] =>
  [...CHANNEL_ORDER].sort((a, b) => settings.channels[a].priority - settings.channels[b].priority);

const normalizeSettings = (settings: NotificationSettings): NotificationPreferencesState => ({
  ...settings,
  reportTypes: {
    ...(settings.reportTypes ?? {}),
    // Absent means the server has the report type enabled by default.
    standaloneDailyReport: settings.reportTypes?.standaloneDailyReport ?? true,
  },
});

const NotificationPreferences: React.FC = () => {
  const [settings, setSettings] = useState<NotificationPreferencesState | null>(null);
  const [savedSettings, setSavedSettings] = useState<NotificationPreferencesState | null>(null);
  const [order, setOrder] = useState<NotificationChannel[]>(CHANNEL_ORDER);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchNotificationSettings(), fetchLinkedAccounts()])
      .then(([fetchedSettings, accounts]) => {
        const normalizedSettings = normalizeSettings(fetchedSettings);
        setSettings(normalizedSettings);
        setSavedSettings(normalizedSettings);
        setOrder(sortByPriority(normalizedSettings));
        setLinkedAccounts(accounts);
      })
      .catch(() => setError('Failed to load notification settings.'))
      .finally(() => setIsLoading(false));
  }, []);

  const isLinked = (channel: NotificationChannel) => linkedAccounts.some((a) => a.channel === channel);

  const isDirty = !!settings && !!savedSettings && JSON.stringify(settings) !== JSON.stringify(savedSettings);

  const toggleGlobal = () => {
    if (!settings) return;
    setSettings({ ...settings, enabled: !settings.enabled });
  };

  const toggleChannel = (channel: NotificationChannel) => {
    if (!settings) return;
    setSettings({
      ...settings,
      channels: {
        ...settings.channels,
        [channel]: { ...settings.channels[channel], enabled: !settings.channels[channel].enabled },
      },
    });
  };

  const toggleStandaloneDailyReport = () => {
    if (!settings) return;
    setSettings({
      ...settings,
      reportTypes: {
        ...settings.reportTypes,
        standaloneDailyReport: !settings.reportTypes.standaloneDailyReport,
      },
    });
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (!settings || target < 0 || target >= order.length) return;
    const nextOrder = [...order];
    [nextOrder[index], nextOrder[target]] = [nextOrder[target], nextOrder[index]];
    setOrder(nextOrder);
    const nextChannels = { ...settings.channels };
    nextOrder.forEach((channel, i) => {
      nextChannels[channel] = { ...nextChannels[channel], priority: i + 1 };
    });
    setSettings({ ...settings, channels: nextChannels });
  };

  const handleSave = async () => {
    if (!settings || !savedSettings) return;
    setIsSaving(true);

    try {
      const saved = await updateNotificationSettings(settings);
      const normalizedSaved = normalizeSettings(saved);
      setSettings(normalizedSaved);
      setSavedSettings(normalizedSaved);
      setOrder(sortByPriority(normalizedSaved));
      toast.success('Notification preferences saved.');
    } catch {
      toast.error('Failed to save notification preferences.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4 space-y-3">
          <div className="skeleton h-5 w-48" />
          <div className="skeleton h-12 w-full" />
          <div className="skeleton h-12 w-full" />
          <div className="skeleton h-12 w-full" />
          <div className="skeleton h-12 w-full" />
        </div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="alert alert-error shadow-card">
        <span>{error ?? 'Failed to load notification settings.'}</span>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4 gap-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">Notifications</div>
            <div className="text-sm opacity-70 max-w-lg">
              Sync failures, the daily risk digest, weekly summary, and proactive insights go to your highest-priority
              enabled channel only. If that send fails, MFlow falls back to the next one.
            </div>
          </div>
          <input
            type="checkbox"
            className="toggle toggle-primary shrink-0"
            checked={settings.enabled}
            onChange={toggleGlobal}
            title="Enable notifications"
          />
        </div>

        <div className={clsx('space-y-3', !settings.enabled && 'opacity-50 pointer-events-none')}>
          <div className="space-y-2">
            <div className="text-xs font-semibold opacity-50 uppercase tracking-wide px-1">Report types</div>
            <div className="flex items-center gap-3 rounded-lg bg-base-200 px-3 py-2.5">
              <span className="text-lg shrink-0 text-amber-500">
                <LuFileText />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">Daily Standalone Project Digest</div>
                <div className="text-xs opacity-60">
                  A daily summary of your Standalone projects, delivered to your highest-priority enabled channel.
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-sm toggle-primary shrink-0"
                checked={settings.reportTypes.standaloneDailyReport}
                onChange={toggleStandaloneDailyReport}
                title="Enable Daily Standalone Project Digest"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold opacity-50 uppercase tracking-wide px-1">Channels</div>
            {order.map((channel, index) => {
              const meta = CHANNEL_META[channel];
              const channelSettings = settings.channels[channel];
              const linked = isLinked(channel);
              return (
                <div key={channel} className="flex items-center gap-3 rounded-lg bg-base-200 px-3 py-2.5">
                  <div className="flex flex-col shrink-0">
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs px-1 min-h-0 h-4"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      title="Move up"
                    >
                      <LuArrowUp className="text-xs" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs px-1 min-h-0 h-4"
                      disabled={index === order.length - 1}
                      onClick={() => move(index, 1)}
                      title="Move down"
                    >
                      <LuArrowDown className="text-xs" />
                    </button>
                  </div>
                  <span className="badge badge-sm font-mono shrink-0">#{index + 1}</span>
                  <span className={clsx('text-lg shrink-0', meta.iconClassName)}>{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{meta.label}</div>
                    {!linked && (
                      <div className="text-xs opacity-60">
                        Not linked —{' '}
                        <Link to="/integrations" className="link link-primary">
                          connect in Integrations
                        </Link>
                      </div>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-sm toggle-primary shrink-0"
                    checked={channelSettings.enabled}
                    onChange={() => toggleChannel(channel)}
                    title={`Enable ${meta.label}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <button className="btn btn-primary btn-sm" disabled={!isDirty || isSaving} onClick={handleSave}>
            {isSaving ? <span className="loading loading-spinner loading-xs" /> : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
